import {isPlatformBrowser} from '@angular/common';
import {HttpBackend, HttpClient, HttpErrorResponse, HttpRequest} from '@angular/common/http';
import {Inject, Injectable, NgZone, PLATFORM_ID, signal} from '@angular/core';
import {App} from '@capacitor/app';
import type {PluginListenerHandle} from '@capacitor/core';
import {firstValueFrom, Subject, timeout, TimeoutError} from 'rxjs';
import {isNativeCapacitorPlatform} from '../platform';
import {buildApiUrl} from './api-url';
import {apiErrorDetails} from '../errors/api-error-details';
import requiredContracts from './api-required-contracts.json';

export type ApiCompatibilityState = 'checking' | 'compatible' | 'incompatible' | 'unverified' | 'unavailable';
export type ApiCompatibilityFailureState = Exclude<ApiCompatibilityState, 'checking' | 'compatible'>;

export class ApiCompatibilityBlockedError extends Error {
  constructor(readonly state: Exclude<ApiCompatibilityState, 'compatible'>) {
    super(`API compatibility is ${state}`);
    this.name = 'ApiCompatibilityBlockedError';
  }
}

interface VersionResponseDto {
  success: boolean;
  data?: {compatibility?: unknown} | null;
}

interface CompatibilityManifest {
  schemaVersion: 1;
  supportedContracts: {application: number[]; basket: number[]};
}

export interface CompatibilityAppStatePort {
  addListener(eventName: 'appStateChange', listener: (state: {isActive: boolean}) => void): Promise<PluginListenerHandle>;
}

function isVersionList(value: unknown): value is number[] {
  return Array.isArray(value) && value.every(version => Number.isInteger(version) && version > 0);
}

function readManifest(value: unknown): CompatibilityManifest | null {
  if (!value || typeof value !== 'object') return null;
  const manifest = value as Partial<CompatibilityManifest>;
  if (manifest.schemaVersion !== 1 || !manifest.supportedContracts || typeof manifest.supportedContracts !== 'object'
    || !isVersionList(manifest.supportedContracts.application)
    || !isVersionList(manifest.supportedContracts.basket)) return null;
  return manifest as CompatibilityManifest;
}

@Injectable({providedIn: 'root'})
export class ApiCompatibilityService {
  readonly active = signal(false);
  readonly state = signal<ApiCompatibilityState>('checking');
  readonly serverUnavailable = signal(false);
  readonly failureDetails = signal('');
  readonly recoveryVisible = signal(false);
  readonly retryCountdownSeconds = signal(0);
  readonly retryDelaySeconds = signal(0);
  // Keeps a completed recovery probe's HTTP failure visible even on a public route.
  readonly recoveryRequested = signal(false);
  private readonly recoveredSubject = new Subject<void>();
  readonly recovered$ = this.recoveredSubject.asObservable();

  private readonly client: HttpClient;
  private inFlight?: Promise<ApiCompatibilityState>;
  private readonly readyWaiters = new Set<() => void>();
  private initialized = false;
  private nativeListener?: PluginListenerHandle;
  private retryTimer?: ReturnType<typeof setInterval>;
  private retryIndex = 0;
  private retryableHttpProbeFailure = false;
  private destroyed = false;
  private readonly visibilityListener = () => {
    if (document.visibilityState === 'visible' && this.active()) void this.recheck();
  };

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object, backend: HttpBackend,
    private readonly ngZone: NgZone) {
    // The public probe must not enter auth, error-dialog, or write-gate interceptors.
    this.client = new HttpClient(backend);
  }

  async initialize(app: CompatibilityAppStatePort = App, native = isNativeCapacitorPlatform()): Promise<void> {
    if (this.initialized || !isPlatformBrowser(this.platformId)) return;
    this.initialized = true;
    this.destroyed = false;
    if (native) {
      try {
        const listener = await app.addListener('appStateChange', ({isActive}) => {
          if (isActive && this.active()) this.ngZone.run(() => { void this.recheck(); });
        });
        if (!this.initialized) { await listener.remove(); return; }
        this.nativeListener = listener;
      } catch { /* A cold-start probe still runs through the protected route. */ }
    } else {
      document.addEventListener('visibilitychange', this.visibilityListener);
    }
  }

  async destroy(): Promise<void> {
    this.initialized = false;
    this.destroyed = true;
    this.stopRecovery();
    if (isPlatformBrowser(this.platformId)) document.removeEventListener('visibilitychange', this.visibilityListener);
    await this.nativeListener?.remove();
    this.nativeListener = undefined;
  }

  /** Protected navigation stays pending until a verified compatible manifest is observed. */
  async waitUntilCompatible(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) return true;
    const state = await this.ensureChecked();
    if (state === 'compatible') return true;
    return new Promise(resolve => {
      const complete = () => resolve(true);
      this.readyWaiters.add(complete);
      if (this.state() === 'compatible') {
        this.readyWaiters.delete(complete);
        complete();
      }
    });
  }

  /** A blocked write fails once; later Retry only repeats this read-only probe. */
  async ensureChecked(): Promise<ApiCompatibilityState> {
    if (!isPlatformBrowser(this.platformId)) return 'compatible';
    if (this.inFlight) return this.inFlight;
    if (this.active()) return this.state();
    return this.recheck();
  }

  recheck(): Promise<ApiCompatibilityState> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve('compatible');
    if (this.inFlight) return this.inFlight;
    this.cancelRetryTimer();
    this.retryCountdownSeconds.set(0);
    this.retryableHttpProbeFailure = false;
    this.active.set(true);
    this.failureDetails.set('');
    this.state.set('checking');
    this.inFlight = this.probe().then(state => {
      this.state.set(state);
      this.finishRecoveryProbe(state);
      if (state === 'compatible') {
        for (const resolve of this.readyWaiters) resolve();
        this.readyWaiters.clear();
      }
      return state;
    }).finally(() => { this.inFlight = undefined; });
    return this.inFlight;
  }

  private async probe(): Promise<ApiCompatibilityState> {
    try {
      const response = await firstValueFrom(this.client.get<VersionResponseDto>(
        buildApiUrl('/v1/system/version'), {observe: 'response', params: {_compatibilityCheck: String(Date.now())}},
      ).pipe(timeout(5000)));
      this.serverUnavailable.set(false);
      if (response.status !== 200 || response.body?.success !== true) return 'unverified';
      const manifest = readManifest(response.body.data?.compatibility);
      if (!manifest) return 'unverified';
      return requiredContracts.supportedContracts.application.every(version => manifest.supportedContracts.application.includes(version))
        && requiredContracts.supportedContracts.basket.every(version => manifest.supportedContracts.basket.includes(version))
        ? 'compatible' : 'incompatible';
    } catch (error) {
      this.retryableHttpProbeFailure = error instanceof HttpErrorResponse && error.status >= 500;
      this.serverUnavailable.set(error instanceof TimeoutError
        || (error instanceof HttpErrorResponse && error.status === 0));
      if (error instanceof HttpErrorResponse) {
        this.failureDetails.set(apiErrorDetails(new HttpRequest('GET', buildApiUrl('/v1/system/version')), error));
      }
      return 'unavailable';
    }
  }

  /** Ordinary failed requests are never replayed; only the public version GET is retried. */
  reportUnavailable(): void {
    if (!isPlatformBrowser(this.platformId) || this.destroyed || this.recoveryVisible()) return;
    this.active.set(true);
    this.serverUnavailable.set(true);
    this.recoveryRequested.set(true);
    this.recoveryVisible.set(true);
    if (!this.inFlight) {
      this.state.set('unavailable');
      this.scheduleRetry();
    }
  }

  private finishRecoveryProbe(state: ApiCompatibilityState): void {
    if (this.destroyed) return;
    if (state === 'unavailable' && (this.serverUnavailable()
      || (this.recoveryVisible() && this.retryableHttpProbeFailure))) {
      this.recoveryRequested.set(true);
      this.recoveryVisible.set(true);
      this.scheduleRetry();
      return;
    }
    const recovered = this.recoveryRequested();
    this.stopRecovery();
    if (state === 'compatible') {
      this.recoveryRequested.set(false);
      if (recovered) this.recoveredSubject.next();
    }
  }

  private scheduleRetry(): void {
    this.cancelRetryTimer();
    const delay = [5, 10, 30][Math.min(this.retryIndex++, 2)];
    this.retryDelaySeconds.set(delay);
    this.retryCountdownSeconds.set(delay);
    const deadline = Date.now() + delay * 1000;
    this.retryTimer = setInterval(() => {
      const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      this.retryCountdownSeconds.set(seconds);
      if (seconds === 0) void this.recheck();
    }, 1000);
  }

  private cancelRetryTimer(): void {
    if (this.retryTimer !== undefined) clearInterval(this.retryTimer);
    this.retryTimer = undefined;
  }

  private stopRecovery(): void {
    this.cancelRetryTimer();
    this.recoveryVisible.set(false);
    this.retryCountdownSeconds.set(0);
    this.retryDelaySeconds.set(0);
    this.retryIndex = 0;
  }
}
